import { useCallback, useState } from "react";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { AIJobStartResponse } from "../types/compassAITypes";
import { BatchAssessEngagementV2Request } from "../types/engagementAssessmentV2Types";
import { CompassError } from "../types/errorTypes";
import { MESSAGES } from "../messages";

interface UseAssessEngagementsBatchV2Return {
  triggerBatchAssessment: (
    payload: BatchAssessEngagementV2Request,
  ) => Promise<string | null>;
  jobId: string | null;
  isLoading: boolean;
  error: CompassError | null;
  reset: () => void;
}

export function useAssessEngagementsBatchV2(): UseAssessEngagementsBatchV2Return {
  const { addToast } = useToast();

  const {
    sendData: postBatchAssessment,
    isLoading,
    error,
  } = usePost<AIJobStartResponse, BatchAssessEngagementV2Request>();

  const [jobId, setJobId] = useState<string | null>(null);

  const triggerBatchAssessment = useCallback(
    async (
      payload: BatchAssessEngagementV2Request,
    ): Promise<string | null> => {
      setJobId(null);

      const response = await postBatchAssessment(
        "AI_ASSESS_ENGAGEMENTS_BATCH_V2",
        payload,
      );

      if (response?.job_id) {
        setJobId(response.job_id);
        return response.job_id;
      }

      addToast(
        MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.BATCH_TRIGGER_FAILED,
        "error",
      );
      return null;
    },
    [postBatchAssessment, addToast],
  );

  const reset = useCallback((): void => {
    setJobId(null);
  }, []);

  return {
    triggerBatchAssessment,
    jobId,
    isLoading,
    error,
    reset,
  };
}
